import { Currency, Percent } from '@kodiak-finance/sdk-core'
import IUniswapV2Router02Json from '@kodiak-finance/v2-periphery/build/IUniswapV2Router02.json'
import { useQueryClient } from '@tanstack/react-query'
import { V2_ROUTER_ADDRESSES } from 'constants/addresses'
import { v2QueryKeys } from 'hooks/subgraph/v2'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import { useResolveCurrencyAddress } from 'hooks/useResolveCurrencyAddress'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { V2Wrapper } from 'pages/AddLiquidityV2'
import { useCallback, useContext, useMemo, useState } from 'react'
import { ArrowDown, Plus } from 'react-feather'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import type { Abi, Address } from 'viem'

import { ButtonConfirmed, ButtonError, ButtonLight, ButtonPrimary } from '../../components/Button'
import { BlueCard, LightCard } from '../../components/Card'
import { AutoColumn, ColumnCenter } from '../../components/Column'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import CurrencyLogo from '../../components/CurrencyLogo'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import { AddRemoveTabs } from '../../components/NavigationTabs'
import { MinimalPositionCard } from '../../components/PositionCard'
import Row, { RowBetween, RowFixed } from '../../components/Row'
import Slider from '../../components/Slider'
import { Dots } from '../../components/swap/styleds'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from '../../components/TransactionConfirmationModal'
import { WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import useDebouncedChangeHandler from '../../hooks/useDebouncedChangeHandler'
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { useToggleWalletModal } from '../../state/application/hooks'
import { Field } from '../../state/burn/actions'
import { useBurnActionHandlers, useBurnState, useDerivedBurnInfo } from '../../state/burn/hooks'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { TransactionType } from '../../state/transactions/types'
import { useUserSlippageToleranceWithDefault } from '../../state/user/hooks'
import { StyledInternalLink, ThemedText } from '../../theme'
import { calculateSlippageAmount } from '../../utils/calculateSlippageAmount'
import { currencyId } from '../../utils/currencyId'
import { ClickableText, MaxButton } from '../Pool/styleds'
import { Wrapper } from './styled'

const { abi: IUniswapV2Router02ABI } = IUniswapV2Router02Json

const DEFAULT_REMOVE_LIQUIDITY_SLIPPAGE_TOLERANCE = new Percent(5, 100)

interface TransactionState {
  attemptingTxn: boolean
  errorMessage: string | undefined
  txHash: string | undefined
  transactionWaiting?: boolean
}

export default function RemoveLiquidity() {
  const navigate = useChainAwareNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { currencyIdA, currencyIdB } = useParams<{ currencyIdA: string; currencyIdB: string }>()
  const resolvedCurrencyIdA = useResolveCurrencyAddress(currencyIdA)
  const resolvedCurrencyIdB = useResolveCurrencyAddress(currencyIdB)

  const [currencyA, currencyB] = [
    useCurrency(resolvedCurrencyIdA) ?? undefined,
    useCurrency(
      resolvedCurrencyIdA?.toLowerCase() !== resolvedCurrencyIdB?.toLowerCase() ? resolvedCurrencyIdB : undefined
    ) ?? undefined,
  ]
  const { account, chainId, wagmiWalletClient, wagmiPublicClient } = useConnection()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const [tokenA, tokenB] = useMemo(() => [currencyA?.wrapped, currencyB?.wrapped], [currencyA, currencyB])

  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  const toggleWalletModal = useToggleWalletModal()

  // burn state
  const { independentField, typedValue } = useBurnState()
  const { pair, parsedAmounts, liquidityTokenBalance, totalSupply, error } = useDerivedBurnInfo(
    currencyA ?? undefined,
    currencyB ?? undefined
  )
  const { onUserInput: _onUserInput } = useBurnActionHandlers()
  const isValid = !error

  // modal and loading
  const [transactionState, setTransactionState] = useState<TransactionState>({
    attemptingTxn: false,
    errorMessage: undefined,
    txHash: undefined,
    transactionWaiting: false,
  })

  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [showDetailed, setShowDetailed] = useState<boolean>(false)

  // txn values
  const deadline = useTransactionDeadline()
  const allowedSlippage = useUserSlippageToleranceWithDefault(DEFAULT_REMOVE_LIQUIDITY_SLIPPAGE_TOLERANCE)

  const formattedAmounts = {
    [Field.LIQUIDITY_PERCENT]: parsedAmounts[Field.LIQUIDITY_PERCENT].equalTo('0')
      ? '0'
      : parsedAmounts[Field.LIQUIDITY_PERCENT].lessThan(new Percent('1', '100'))
      ? '<1'
      : parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0),
    [Field.LIQUIDITY]:
      independentField === Field.LIQUIDITY ? typedValue : parsedAmounts[Field.LIQUIDITY]?.toSignificant(6) ?? '',
    [Field.CURRENCY_A]:
      independentField === Field.CURRENCY_A ? typedValue : parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) ?? '',
    [Field.CURRENCY_B]:
      independentField === Field.CURRENCY_B ? typedValue : parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) ?? '',
  }

  const atMaxAmount = parsedAmounts[Field.LIQUIDITY_PERCENT]?.equalTo(new Percent('1'))

  const routerAddress = V2_ROUTER_ADDRESSES[chainId] as Address | undefined

  // allowance handling
  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.LIQUIDITY], routerAddress)

  async function onAttemptToApprove() {
    if (!pair || !wagmiWalletClient || !deadline) throw new Error('missing dependencies')
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY]
    if (!liquidityAmount) throw new Error('missing liquidity amount')

    await approveCallback()
  }

  // wrapped onUserInput to clear signatures
  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      return _onUserInput(field, typedValue)
    },
    [_onUserInput]
  )

  const onLiquidityInput = useCallback(
    (typedValue: string): void => onUserInput(Field.LIQUIDITY, typedValue),
    [onUserInput]
  )
  const onCurrencyAInput = useCallback(
    (typedValue: string): void => onUserInput(Field.CURRENCY_A, typedValue),
    [onUserInput]
  )
  const onCurrencyBInput = useCallback(
    (typedValue: string): void => onUserInput(Field.CURRENCY_B, typedValue),
    [onUserInput]
  )

  // tx sending
  const addTransaction = useTransactionAdder()
  const { writeContractAsync } = useWriteContract()

  async function onRemove() {
    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })

    try {
      if (!chainId || !wagmiWalletClient || !wagmiPublicClient || !account || !deadline || !routerAddress)
        throw new Error('missing dependencies')
      const { [Field.CURRENCY_A]: currencyAmountA, [Field.CURRENCY_B]: currencyAmountB } = parsedAmounts
      if (!currencyAmountA || !currencyAmountB) {
        throw new Error('missing currency amounts')
      }

      const amountsMin = {
        [Field.CURRENCY_A]: calculateSlippageAmount(currencyAmountA, allowedSlippage)[0],
        [Field.CURRENCY_B]: calculateSlippageAmount(currencyAmountB, allowedSlippage)[0],
      }

      if (!currencyA || !currencyB) throw new Error('missing tokens')
      const liquidityAmount = parsedAmounts[Field.LIQUIDITY]
      if (!liquidityAmount) throw new Error('missing liquidity amount')

      const currencyBIsETH = currencyB.isNative
      const oneCurrencyIsETH = currencyA.isNative || currencyBIsETH

      if (!tokenA || !tokenB) throw new Error('could not wrap')

      let methodNames: string[], args: Array<string | string[] | number | boolean | bigint>
      // we have approval, use normal remove liquidity
      if (approval === ApprovalState.APPROVED) {
        // removeLiquidityETH
        if (oneCurrencyIsETH) {
          methodNames = ['removeLiquidityETH', 'removeLiquidityETHSupportingFeeOnTransferTokens']
          args = [
            currencyBIsETH ? tokenA.address : tokenB.address,
            BigInt(liquidityAmount.quotient.toString()),
            BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B].toString()),
            BigInt(amountsMin[currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A].toString()),
            account,
            deadline,
          ]
        }
        // removeLiquidity
        else {
          methodNames = ['removeLiquidity']
          args = [
            tokenA.address as Address,
            tokenB.address as Address,
            BigInt(liquidityAmount.quotient.toString()),
            BigInt(amountsMin[Field.CURRENCY_A].toString()),
            BigInt(amountsMin[Field.CURRENCY_B].toString()),
            account,
            deadline,
          ]
        }
      } else {
        throw new Error('Attempting to confirm without approval. Please contact support.')
      }

      // Try to simulate all methods and find the first successful one
      const simulationResults = await Promise.allSettled(
        methodNames.map((methodName) =>
          wagmiPublicClient.simulateContract({
            address: routerAddress,
            abi: IUniswapV2Router02ABI as Abi,
            functionName: methodName,
            args,
            account,
          })
        )
      )

      const indexOfSuccessfulSimulation = simulationResults.findIndex((result) => result.status === 'fulfilled')

      // all simulations failed...
      if (indexOfSuccessfulSimulation === -1) {
        const firstError = simulationResults[0]?.status === 'rejected' ? simulationResults[0].reason : null
        const errorMessage = firstError?.shortMessage || firstError?.message || 'Transaction simulation failed'
        throw new Error(`This transaction would fail. ${errorMessage}`)
      }

      const successfulSimulation = simulationResults[indexOfSuccessfulSimulation]
      if (successfulSimulation.status !== 'fulfilled') {
        throw new Error('Unexpected error: simulation result is not fulfilled')
      }
      // Send the transaction using the successful simulation
      const hash = await writeContractAsync(successfulSimulation.value.request)

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: hash,
        transactionWaiting: true,
      })

      addTransaction(
        { hash },
        {
          type: TransactionType.REMOVE_LIQUIDITY_V3,
          baseCurrencyId: currencyId(currencyA),
          quoteCurrencyId: currencyId(currencyB),
          expectedAmountBaseRaw: parsedAmounts[Field.CURRENCY_A]?.quotient.toString() ?? '0',
          expectedAmountQuoteRaw: parsedAmounts[Field.CURRENCY_B]?.quotient.toString() ?? '0',
        }
      )

      // Wait for transaction receipt (1 confirmation)
      const receipt = await waitForTransactionReceipt({ hash })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: receipt.transactionHash,
        transactionWaiting: false,
      })

      if (account) {
        await queryClient.invalidateQueries({ queryKey: ['v2'] })

        if (pair?.liquidityToken?.address) {
          const pairAddress = pair.liquidityToken.address.toLowerCase()
          await queryClient.invalidateQueries({
            queryKey: v2QueryKeys.userPositionByPair(chainId, account.toLowerCase(), pairAddress, false),
          })
        }
      }
    } catch (error: any) {
      setTransactionState({
        attemptingTxn: false,
        errorMessage: error?.reason || error?.message || 'Remove liquidity failed',
        txHash: undefined,
        transactionWaiting: false,
      })

      // Log the error for debugging
      console.error('Remove liquidity error:', error)
    }
  }

  function modalHeader() {
    return (
      <AutoColumn gap="md" style={{ marginTop: '20px' }}>
        <RowBetween align="flex-end">
          <Text fontSize={24} fontWeight={500}>
            {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)}
          </Text>
          <RowFixed gap="4px">
            <CurrencyLogo currency={currencyA} size="24px" />
            <Text fontSize={24} fontWeight={500} style={{ marginLeft: '10px' }}>
              {currencyA?.symbol}
            </Text>
          </RowFixed>
        </RowBetween>
        <RowFixed>
          <Plus size="16" color={theme.deprecated_text2} />
        </RowFixed>
        <RowBetween align="flex-end">
          <Text fontSize={24} fontWeight={500}>
            {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)}
          </Text>
          <RowFixed gap="4px">
            <CurrencyLogo currency={currencyB} size="24px" />
            <Text fontSize={24} fontWeight={500} style={{ marginLeft: '10px' }}>
              {currencyB?.symbol}
            </Text>
          </RowFixed>
        </RowBetween>

        <ThemedText.DeprecatedItalic fontSize={12} color={theme.deprecated_text2} textAlign="left" padding="12px 0 0 0">
          <>
            Output is estimated. If the price changes by more than {allowedSlippage.toSignificant(4)}% your transaction
            will revert.
          </>
        </ThemedText.DeprecatedItalic>
      </AutoColumn>
    )
  }

  function modalBottom() {
    return (
      <>
        <RowBetween>
          <Text color={theme.deprecated_text2} fontWeight={500} fontSize={16}>
            <>
              UNI {currencyA?.symbol}/{currencyB?.symbol} Burned
            </>
          </Text>
          <RowFixed>
            <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} margin={true} />
            <Text fontWeight={500} fontSize={16}>
              {parsedAmounts[Field.LIQUIDITY]?.toSignificant(6)}
            </Text>
          </RowFixed>
        </RowBetween>
        {pair && (
          <>
            <RowBetween>
              <Text color={theme.deprecated_text2} fontWeight={500} fontSize={16}>
                <>Price</>
              </Text>
              <Text fontWeight={500} fontSize={16} color={theme.deprecated_text1}>
                1 {currencyA?.symbol} = {tokenA ? pair.priceOf(tokenA).toSignificant(6) : '-'} {currencyB?.symbol}
              </Text>
            </RowBetween>
            <RowBetween>
              <div />
              <Text fontWeight={500} fontSize={16} color={theme.deprecated_text1}>
                1 {currencyB?.symbol} = {tokenB ? pair.priceOf(tokenB).toSignificant(6) : '-'} {currencyA?.symbol}
              </Text>
            </RowBetween>
          </>
        )}
        <ButtonPrimary disabled={!(approval === ApprovalState.APPROVED)} onClick={onRemove}>
          <Text fontWeight={500} fontSize={20}>
            <>Confirm</>
          </Text>
        </ButtonPrimary>
      </>
    )
  }

  const pendingText = (
    <>
      Removing {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencyA?.symbol} and{' '}
      {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencyB?.symbol}
    </>
  )

  const liquidityPercentChangeCallback = useCallback(
    (value: number) => {
      onUserInput(Field.LIQUIDITY_PERCENT, value.toString())
    },
    [onUserInput]
  )

  const oneCurrencyIsETH = currencyA?.isNative || currencyB?.isNative

  const oneCurrencyIsWETH = Boolean(
    chainId &&
      WRAPPED_NATIVE_CURRENCY[chainId] &&
      ((currencyA && WRAPPED_NATIVE_CURRENCY[chainId]?.equals(currencyA)) ||
        (currencyB && WRAPPED_NATIVE_CURRENCY[chainId]?.equals(currencyB)))
  )

  const handleSelectCurrencyA = useCallback(
    (currency: Currency) => {
      if (resolvedCurrencyIdB && currencyId(currency, chainId) === resolvedCurrencyIdB) {
        navigate(`/liquidity/v2/remove/${currencyId(currency, chainId)}/${resolvedCurrencyIdA}`)
      } else {
        navigate(`/liquidity/v2/remove/${currencyId(currency, chainId)}/${resolvedCurrencyIdB}`)
      }
    },
    [resolvedCurrencyIdA, resolvedCurrencyIdB, navigate, chainId]
  )
  const handleSelectCurrencyB = useCallback(
    (currency: Currency) => {
      if (resolvedCurrencyIdA && currencyId(currency, chainId) === resolvedCurrencyIdA) {
        navigate(`/liquidity/v2/remove/${resolvedCurrencyIdB}/${currencyId(currency, chainId)}`)
      } else {
        navigate(`/liquidity/v2/remove/${resolvedCurrencyIdA}/${currencyId(currency, chainId)}`)
      }
    },
    [resolvedCurrencyIdA, resolvedCurrencyIdB, navigate, chainId]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (transactionState.txHash) {
      onUserInput(Field.LIQUIDITY_PERCENT, '0')
    }

    setTransactionState({
      attemptingTxn: false,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })
  }, [onUserInput, transactionState.txHash])

  const confirmationContent = useCallback(() => {
    if (transactionState.errorMessage) {
      return <TransactionErrorContent onDismiss={handleDismissConfirmation} message={transactionState.errorMessage} />
    }
    if (transactionState.txHash) {
      return (
        <ConfirmationModalContent
          title={<>You will receive</>}
          onDismiss={handleDismissConfirmation}
          topContent={() => (
            <div>
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Transaction Submitted</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Removing {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencyA?.symbol} and{' '}
                  {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencyB?.symbol}
                </div>
              </div>
            </div>
          )}
          bottomContent={() => null}
        />
      )
    }
    return (
      <ConfirmationModalContent
        title={<>You will receive</>}
        onDismiss={handleDismissConfirmation}
        topContent={modalHeader}
        bottomContent={modalBottom}
      />
    )
  }, [
    transactionState.errorMessage,
    transactionState.txHash,
    handleDismissConfirmation,
    parsedAmounts,
    currencyA?.symbol,
    currencyB?.symbol,
  ])

  const [innerLiquidityPercentage, setInnerLiquidityPercentage] = useDebouncedChangeHandler(
    Number.parseInt(parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0)),
    liquidityPercentChangeCallback
  )

  return (
    <>
      <V2Wrapper>
        <AddRemoveTabs creating={false} adding={false} defaultSlippage={DEFAULT_REMOVE_LIQUIDITY_SLIPPAGE_TOLERANCE} />
        <Wrapper>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={transactionState.attemptingTxn}
            hash={transactionState.txHash || ''}
            transactionWaiting={transactionState.transactionWaiting}
            content={confirmationContent}
            pendingText={pendingText}
          />
          <AutoColumn gap="md" style={{ width: '100%' }}>
            <BlueCard>
              <AutoColumn gap="10px" style={{ width: '100%' }}>
                <ThemedText.DeprecatedLink fontWeight={400} color="deprecated_primaryText1">
                  <>Accrued fees are included in the received tokens.</>
                </ThemedText.DeprecatedLink>
              </AutoColumn>
            </BlueCard>
            {pair ? (
              <AutoColumn style={{ minWidth: '20rem', width: '100%', maxWidth: '446px', marginTop: '1rem' }}>
                <MinimalPositionCard
                  showUnwrapped={oneCurrencyIsWETH}
                  pair={pair}
                  totalSupply={totalSupply}
                  liquidityTokenBalance={liquidityTokenBalance}
                />
              </AutoColumn>
            ) : null}
            <LightCard>
              <AutoColumn gap="20px">
                <RowBetween>
                  <Text fontWeight={500}>
                    <>Remove Amount</>
                  </Text>
                  <ClickableText
                    fontWeight={500}
                    onClick={() => {
                      setShowDetailed(!showDetailed)
                    }}
                  >
                    {showDetailed ? <>Simple</> : <>Detailed</>}
                  </ClickableText>
                </RowBetween>
                <Row style={{ alignItems: 'flex-end' }}>
                  <Text fontSize={30} fontWeight={500}>
                    {formattedAmounts[Field.LIQUIDITY_PERCENT]}%
                  </Text>
                </Row>
                {!showDetailed && (
                  <>
                    <Slider value={innerLiquidityPercentage} onChange={setInnerLiquidityPercentage} />
                    <RowBetween>
                      <MaxButton onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '25')} width="20%">
                        25%
                      </MaxButton>
                      <MaxButton onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '50')} width="20%">
                        50%
                      </MaxButton>
                      <MaxButton onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '75')} width="20%">
                        75%
                      </MaxButton>
                      <MaxButton onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')} width="20%">
                        Max
                      </MaxButton>
                    </RowBetween>
                  </>
                )}
              </AutoColumn>
            </LightCard>
            {!showDetailed && (
              <>
                <ColumnCenter>
                  <ArrowDown size="16" color={theme.deprecated_text2} />
                </ColumnCenter>
                <LightCard>
                  <AutoColumn gap="10px">
                    <RowBetween>
                      <Text fontSize={18} fontWeight={500}>
                        {formattedAmounts[Field.CURRENCY_A] || '-'}
                      </Text>
                      <RowFixed>
                        <CurrencyLogo currency={currencyA} className="mr-3" />
                        <Text fontSize={18} fontWeight={500} id="remove-liquidity-tokena-symbol">
                          {currencyA?.symbol}
                        </Text>
                      </RowFixed>
                    </RowBetween>
                    <RowBetween>
                      <Text fontSize={18} fontWeight={500}>
                        {formattedAmounts[Field.CURRENCY_B] || '-'}
                      </Text>
                      <RowFixed>
                        <CurrencyLogo currency={currencyB} className="mr-3" />
                        <Text fontSize={18} fontWeight={500} id="remove-liquidity-tokenb-symbol">
                          {currencyB?.symbol}
                        </Text>
                      </RowFixed>
                    </RowBetween>
                    {chainId && (oneCurrencyIsWETH || oneCurrencyIsETH) ? (
                      <RowBetween style={{ justifyContent: 'flex-end' }}>
                        {oneCurrencyIsETH ? (
                          <StyledInternalLink
                            to={`/liquidity/v2/remove/${
                              currencyA?.isNative && chainId && WRAPPED_NATIVE_CURRENCY[chainId]
                                ? WRAPPED_NATIVE_CURRENCY[chainId]?.address
                                : currencyIdA
                            }/${
                              currencyB?.isNative && chainId && WRAPPED_NATIVE_CURRENCY[chainId]
                                ? WRAPPED_NATIVE_CURRENCY[chainId]?.address
                                : currencyIdB
                            }`}
                          >
                            Receive WBERA
                          </StyledInternalLink>
                        ) : oneCurrencyIsWETH ? (
                          <StyledInternalLink
                            to={`/liquidity/v2/remove/${
                              currencyA && WRAPPED_NATIVE_CURRENCY[chainId]?.equals(currencyA) ? 'ETH' : currencyIdA
                            }/${
                              currencyB && WRAPPED_NATIVE_CURRENCY[chainId]?.equals(currencyB) ? 'ETH' : currencyIdB
                            }`}
                          >
                            Receive BERA
                          </StyledInternalLink>
                        ) : null}
                      </RowBetween>
                    ) : null}
                  </AutoColumn>
                </LightCard>
              </>
            )}

            {showDetailed && (
              <>
                <CurrencyInputPanel
                  value={formattedAmounts[Field.LIQUIDITY]}
                  onUserInput={onLiquidityInput}
                  onMax={() => {
                    onUserInput(Field.LIQUIDITY_PERCENT, '100')
                  }}
                  showMaxButton={!atMaxAmount}
                  currency={pair?.liquidityToken}
                  pair={pair}
                  id="liquidity-amount"
                />
                <ColumnCenter>
                  <ArrowDown size="16" color={theme.deprecated_text2} />
                </ColumnCenter>
                <CurrencyInputPanel
                  hideBalance={true}
                  value={formattedAmounts[Field.CURRENCY_A]}
                  onUserInput={onCurrencyAInput}
                  onMax={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')}
                  showMaxButton={!atMaxAmount}
                  currency={currencyA}
                  label="Output"
                  onCurrencySelect={handleSelectCurrencyA}
                  id="remove-liquidity-tokena"
                />
                <ColumnCenter>
                  <Plus size="16" color={theme.deprecated_text2} />
                </ColumnCenter>
                <CurrencyInputPanel
                  hideBalance={true}
                  value={formattedAmounts[Field.CURRENCY_B]}
                  onUserInput={onCurrencyBInput}
                  onMax={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')}
                  showMaxButton={!atMaxAmount}
                  currency={currencyB}
                  label="Output"
                  onCurrencySelect={handleSelectCurrencyB}
                  id="remove-liquidity-tokenb"
                />
              </>
            )}
            {pair && (
              <div style={{ padding: '10px 20px' }}>
                <RowBetween>
                  <>Price:</>
                  <div>
                    1 {currencyA?.symbol} = {tokenA ? pair.priceOf(tokenA).toSignificant(6) : '-'} {currencyB?.symbol}
                  </div>
                </RowBetween>
                <RowBetween>
                  <div />
                  <div>
                    1 {currencyB?.symbol} = {tokenB ? pair.priceOf(tokenB).toSignificant(6) : '-'} {currencyA?.symbol}
                  </div>
                </RowBetween>
              </div>
            )}
            <div style={{ position: 'relative' }}>
              {!account ? (
                <ButtonLight onClick={toggleWalletModal}>
                  <>Connect Wallet</>
                </ButtonLight>
              ) : (
                <RowBetween>
                  <ButtonConfirmed
                    onClick={onAttemptToApprove}
                    confirmed={approval === ApprovalState.APPROVED}
                    disabled={approval !== ApprovalState.NOT_APPROVED}
                    mr="0.5rem"
                    fontWeight={500}
                    fontSize={16}
                  >
                    {approval === ApprovalState.PENDING ? (
                      <Dots>
                        <>Approving</>
                      </Dots>
                    ) : approval === ApprovalState.APPROVED ? (
                      <>Approved</>
                    ) : (
                      <>Approve</>
                    )}
                  </ButtonConfirmed>
                  <ButtonError
                    onClick={() => {
                      setShowConfirm(true)
                    }}
                    disabled={!isValid || approval !== ApprovalState.APPROVED}
                    error={!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]}
                  >
                    <Text fontSize={16} fontWeight={500}>
                      {error || <>Remove</>}
                    </Text>
                  </ButtonError>
                </RowBetween>
              )}
            </div>
          </AutoColumn>
        </Wrapper>
      </V2Wrapper>
    </>
  )
}
